// Gerencia › Panel del negocio (pedido de Rodrigo, 2026-10-25): venta, margen, efectivo, cartera y
// flujo en una pantalla, en vivo (se vuelve a leer cada minuto). Nada se calcula aquí: el backend
// entrega las cifras como texto entero y la pantalla solo las muestra.
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/cliente';
import { PanelError } from '../comun/Estados';
import { decimal, fecha, fechaHora, pesos } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';

const CADA = 60_000;

interface Comparacion {
  actual: string;
  anterior: string;
  variacionPct: string | null;
  actualIncompleto: boolean;
  anteriorIncompleto: boolean;
}
interface Grupo {
  clave: string;
  neto: string;
  documentos: number;
}
interface Margen {
  venta: string;
  costo: string;
  margen: string;
  pct: string | null;
  sinCosto: string;
}
interface Cartera {
  saldo: string;
  vencido: string;
  vencidoPct: string | null;
  tramos: { tramo: string; monto: string }[];
  mayores: { entidad: string; saldo: string; vencido: string }[];
}
export interface Panel {
  hoy: string;
  generadoEn: string;
  fuentes: { inicioErp: string | null; historicoHasta: string | null };
  venta: {
    hoy: { neto: string; documentos: number; clientes: number; vsSemanaPasada: Comparacion };
    semana: Comparacion;
    mes: Comparacion;
    porTipo: Grupo[];
    porCanal: Grupo[];
    porVendedor: Grupo[];
    productos: { sku: string; producto: string; unidades: string; neto: string }[];
    ultimos35: { fecha: string; neto: string; fuente: 'ERP' | 'RANDOM' | 'SIN_DATO' }[];
    meses: { mes: string; neto: string; fuente: string; incompleto: boolean }[];
  };
  margen: { hoy: Margen; mes: Margen };
  caja: {
    efectivoEnCajas: string;
    cajasAbiertas: { punto: string; cajero: string; abiertoEn: string; efectivo: string }[];
    efectivoCajasCerradasHoy: {
      turnos: number;
      esperado: string;
      contado: string;
      diferencia: string;
    };
    cobradoHoy: { medio: string; monto: string; recibos: number }[];
    cheques: { cantidad: number; monto: string; alDia: string; aFecha: string };
    rutaPorImputar: { medio: string; monto: string; cobros: number }[];
  };
  cxc: Cartera;
  cxp: Cartera;
  flujo: {
    inicial: string;
    semanas: {
      desde: string;
      hasta: string;
      entraCxc: string;
      entraCheques: string;
      saleCxp: string;
      neto: string;
      acumulado: string;
    }[];
    fueraDelHorizonte: {
      cxcVencida: string;
      cxpVencida: string;
      cxcDespues: string;
      cxpDespues: string;
    };
  };
}

const NOMBRE_TRAMO: Record<string, string> = {
  corriente: 'Por vencer',
  '1-30': 'Vencido 1–30 días',
  '31-60': 'Vencido 31–60',
  '61-90': 'Vencido 61–90',
  '91+': 'Vencido +90',
};
const NOMBRE_CANAL: Record<string, string> = {
  CALL_CENTER: 'Call center',
  MESON: 'Mesón',
  VENDEDOR: 'Vendedor',
  B2B: 'B2B',
  RUTA: 'Ruta',
  POS: 'POS',
};
const NOMBRE_MEDIO: Record<string, string> = {
  EFECTIVO: 'Efectivo',
  DEBITO: 'Débito',
  CREDITO: 'Crédito',
  TRANSFERENCIA: 'Transferencia',
  CHEQUE: 'Cheque',
};
const DIAS = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
const diaSemana = (f: string) => DIAS[new Date(`${f}T12:00:00Z`).getUTCDay()]!;
const n = (v: string) => Number(v);
/** Pesos abreviados para los ejes y las tarjetas: $12,3 M. */
function millones(v: string): string {
  const x = n(v);
  if (Math.abs(x) >= 1_000_000)
    return `${x < 0 ? '-' : ''}$${decimal((Math.abs(x) / 1e6).toFixed(1))} M`;
  return pesos(v);
}

function Variacion({ c, texto }: { c: Comparacion; texto: string }) {
  if (c.actualIncompleto || c.anteriorIncompleto)
    return (
      <span className="apagado">
        sin comparación: faltan días{' '}
        {c.actualIncompleto ? 'de este período' : 'del período anterior'} en el histórico
      </span>
    );
  if (c.variacionPct === null) return <span className="apagado">sin base para comparar</span>;
  const sube = !c.variacionPct.startsWith('-');
  return (
    <span className={sube ? 'variacion sube' : 'variacion baja'}>
      {sube ? '▲' : '▼'} {decimal(c.variacionPct.replace('-', ''))} % {texto}{' '}
      <span className="apagado">({millones(c.anterior)})</span>
    </span>
  );
}

function Indicador({
  titulo,
  valor,
  children,
}: {
  titulo: string;
  valor: string;
  children?: React.ReactNode;
}) {
  return (
    <fieldset className="recuadro-ficha indicador">
      <legend>{titulo}</legend>
      <div className="indicador-valor">{valor}</div>
      <div className="indicador-detalle">{children}</div>
    </fieldset>
  );
}

function TablaGrupos({ filas, nombre }: { filas: Grupo[]; nombre: (c: string) => string }) {
  const total = filas.reduce((a, f) => a + n(f.neto), 0);
  return (
    <table className="grilla compacta">
      <tbody>
        {filas.map((f) => (
          <tr key={f.clave}>
            <td>{nombre(f.clave)}</td>
            <td className="der">{f.documentos}</td>
            <td className="der">{pesos(f.neto)}</td>
            <td className="der apagado">
              {total ? `${Math.round((n(f.neto) * 100) / total)} %` : ''}
            </td>
          </tr>
        ))}
        {!filas.length && (
          <tr>
            <td colSpan={4} className="grilla-vacia">
              Sin ventas todavía
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

/** Barras de la venta diaria (una serie: el título la nombra). Los días de Random van más claros. */
function GraficoDias({ dias }: { dias: Panel['venta']['ultimos35'] }) {
  const max = Math.max(1, ...dias.map((d) => n(d.neto)));
  const W = 700;
  const H = 150;
  const paso = W / dias.length;
  const ancho = Math.max(4, paso - 4);
  return (
    <svg
      className="grafico-dias"
      viewBox={`0 0 ${W} ${H + 18}`}
      role="img"
      aria-label="Venta neta por día, últimos 35 días"
    >
      <line x1={0} x2={W} y1={H} y2={H} className="eje" />
      {dias.map((d, i) => {
        const h = Math.max(0, (n(d.neto) / max) * (H - 8));
        const x = i * paso + (paso - ancho) / 2;
        return (
          <g key={d.fecha} className={`barra ${d.fuente.toLowerCase()}`}>
            <rect x={i * paso} y={0} width={paso} height={H} className="blanco" />
            {d.fuente !== 'SIN_DATO' && h > 0 && (
              <path
                d={`M${x},${H} v${-Math.max(0, h - 3)} q0,-3 3,-3 h${ancho - 6} q3,0 3,3 v${Math.max(0, h - 3)} z`}
              />
            )}
            <title>
              {`${diaSemana(d.fecha)} ${fecha(d.fecha)}: ${pesos(d.neto)}${d.fuente === 'RANDOM' ? ' (Random)' : d.fuente === 'SIN_DATO' ? ' (sin dato)' : ''}`}
            </title>
            {(i % 7 === dias.length % 7 || i === dias.length - 1) && (
              <text x={i * paso + paso / 2} y={H + 13} textAnchor="middle" className="etiqueta-eje">
                {d.fecha.slice(8)}/{d.fecha.slice(5, 7)}
              </text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function TablaCartera({ c, titulo, entidad }: { c: Cartera; titulo: string; entidad: string }) {
  return (
    <fieldset className="recuadro-ficha">
      <legend>{titulo}</legend>
      <div className="dos-columnas">
        <table className="grilla compacta" aria-label={`${titulo} por antigüedad`}>
          <tbody>
            {c.tramos.map((t) => (
              <tr key={t.tramo}>
                <td>{NOMBRE_TRAMO[t.tramo] ?? t.tramo}</td>
                <td className="der">{pesos(t.monto)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td>
                <b>Total</b>
              </td>
              <td className="der">
                <b>{pesos(c.saldo)}</b>
              </td>
            </tr>
          </tfoot>
        </table>
        <table className="grilla compacta" aria-label={`${titulo}: mayores saldos`}>
          <thead>
            <tr>
              <th>{entidad}</th>
              <th className="der">Saldo</th>
              <th className="der">Vencido</th>
            </tr>
          </thead>
          <tbody>
            {c.mayores.map((m) => (
              <tr key={m.entidad}>
                <td className="recortar">{m.entidad}</td>
                <td className="der">{pesos(m.saldo)}</td>
                <td className="der">{n(m.vencido) ? pesos(m.vencido) : ''}</td>
              </tr>
            ))}
            {!c.mayores.length && (
              <tr>
                <td colSpan={3} className="grilla-vacia">
                  Sin saldos abiertos
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </fieldset>
  );
}

function TablaFlujo({ f }: { f: Panel['flujo'] }) {
  const max = Math.max(1, ...f.semanas.map((s) => Math.abs(n(s.acumulado))));
  return (
    <fieldset className="recuadro-ficha">
      <legend>Proyección de flujo · 13 semanas</legend>
      <table className="grilla compacta flujo" aria-label="Proyección de flujo por semana">
        <thead>
          <tr>
            <th>Semana</th>
            <th className="der">Entra CxC</th>
            <th className="der">Entra cheques</th>
            <th className="der">Sale CxP</th>
            <th className="der">Neto</th>
            <th className="der">Acumulado</th>
            <th aria-hidden />
          </tr>
        </thead>
        <tbody>
          <tr>
            <td colSpan={5} className="apagado">
              Parte del efectivo en cajas abiertas (sin bancos: todavía no hay conexión)
            </td>
            <td className="der">{pesos(f.inicial)}</td>
            <td />
          </tr>
          {f.semanas.map((s) => {
            const a = n(s.acumulado);
            return (
              <tr key={s.desde}>
                <td>
                  {fecha(s.desde).slice(0, 5)} al {fecha(s.hasta).slice(0, 5)}
                </td>
                <td className="der">{n(s.entraCxc) ? pesos(s.entraCxc) : ''}</td>
                <td className="der">{n(s.entraCheques) ? pesos(s.entraCheques) : ''}</td>
                <td className="der">{n(s.saleCxp) ? pesos(`-${s.saleCxp}`) : ''}</td>
                <td className={`der${n(s.neto) < 0 ? ' negativo' : ''}`}>{pesos(s.neto)}</td>
                <td className={`der${a < 0 ? ' negativo' : ''}`}>
                  <b>{pesos(s.acumulado)}</b>
                </td>
                <td className="barra-celda" aria-hidden>
                  <span
                    className={a < 0 ? 'barra-flujo negativa' : 'barra-flujo'}
                    style={{ width: `${(Math.abs(a) / max) * 100}%` }}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <p className="apagado nota">
        Fuera de las semanas: CxC ya vencida {pesos(f.fueraDelHorizonte.cxcVencida)} (no se sabe
        cuándo entra) · CxP ya vencida {pesos(f.fueraDelHorizonte.cxpVencida)} · después de 13
        semanas: entra {pesos(f.fueraDelHorizonte.cxcDespues)}, sale{' '}
        {pesos(f.fueraDelHorizonte.cxpDespues)}. Todavía no incluye sueldos, arriendos, cuotas ni
        cheques girados (compromisos de tesorería).
      </p>
    </fieldset>
  );
}

export function PanelNegocio() {
  const { empresa } = useSesion();
  const empresaId = empresa!.id;
  const q = useQuery({
    queryKey: ['emp', empresaId, 'gerencia', 'panel'],
    queryFn: () => api.get<Panel>('/gerencia/panel', { query: { empresaId } }),
    refetchInterval: CADA,
    refetchIntervalInBackground: false,
  });
  if (q.isError && !q.data) return <PanelError error={q.error} />;
  const p = q.data;
  if (!p) return <p className="apagado">Cargando el panel…</p>;
  const v = p.venta;
  const efectivoRuta = p.caja.rutaPorImputar.reduce((a, r) => a + n(r.monto), 0);
  const cerradas = p.caja.efectivoCajasCerradasHoy;
  const cobradoHoy = p.caja.cobradoHoy.reduce((a, r) => a + n(r.monto), 0);
  return (
    <div className="pantalla consulta panel-negocio" data-pantalla="gerencia-panel">
      <div className="panel-cabecera">
        <h3>
          Panel del negocio · {diaSemana(p.hoy)} {fecha(p.hoy)}
        </h3>
        <span className="apagado">
          Actualizado {fechaHora(p.generadoEn)} · se actualiza solo cada minuto
          {q.isFetching ? ' · leyendo…' : ''}
          {q.isError ? ' · sin conexión, mostrando la última lectura' : ''}
        </span>
      </div>

      <div className="fila-indicadores">
        <Indicador titulo="Venta de hoy (neto)" valor={pesos(v.hoy.neto)}>
          <div>
            {v.hoy.documentos} documentos · {v.hoy.clientes} clientes
          </div>
          <Variacion c={v.hoy.vsSemanaPasada} texto={`vs ${diaSemana(p.hoy)} pasado`} />
        </Indicador>
        <Indicador titulo="Semana (lun a hoy)" valor={millones(v.semana.actual)}>
          <Variacion c={v.semana} texto="vs semana pasada" />
        </Indicador>
        <Indicador titulo="Mes a la fecha" valor={millones(v.mes.actual)}>
          <Variacion c={v.mes} texto="vs mismos días mes anterior" />
        </Indicador>
        <Indicador
          titulo="Margen del mes"
          valor={p.margen.mes.pct === null ? '—' : `${decimal(p.margen.mes.pct)} %`}
        >
          <div>{pesos(p.margen.mes.margen)} sobre costo PMP</div>
          {n(p.margen.mes.sinCosto) > 0 && (
            <div className="apagado">
              {pesos(p.margen.mes.sinCosto)} vendido sin PMP, fuera del cálculo
            </div>
          )}
        </Indicador>
        <Indicador titulo="Efectivo en cajas" valor={pesos(p.caja.efectivoEnCajas)}>
          <div>
            {p.caja.cajasAbiertas.length} caja{p.caja.cajasAbiertas.length === 1 ? '' : 's'} abierta
            {p.caja.cajasAbiertas.length === 1 ? '' : 's'}
          </div>
          {cerradas.turnos > 0 && (
            <div className="apagado">
              + {pesos(cerradas.contado)} contado en {cerradas.turnos} caja
              {cerradas.turnos === 1 ? '' : 's'} cerrada{cerradas.turnos === 1 ? '' : 's'} hoy
            </div>
          )}
          {n(cerradas.diferencia) !== 0 && (
            <div className="alerta-texto">
              Descuadre al cerrar: {pesos(cerradas.diferencia)} (esperado {pesos(cerradas.esperado)}
              )
            </div>
          )}
          {efectivoRuta > 0 && (
            <div className="alerta-texto">{pesos(String(efectivoRuta))} de ruta por imputar</div>
          )}
        </Indicador>
        <Indicador titulo="Cheques en cartera" valor={pesos(p.caja.cheques.monto)}>
          <div>
            {p.caja.cheques.cantidad} cheques · al día {pesos(p.caja.cheques.alDia)}
          </div>
          <div className="apagado">a fecha {pesos(p.caja.cheques.aFecha)}</div>
        </Indicador>
        <Indicador titulo="Clientes nos deben" valor={millones(p.cxc.saldo)}>
          <div className={n(p.cxc.vencido) > 0 ? 'alerta-texto' : undefined}>
            vencido {pesos(p.cxc.vencido)}
            {p.cxc.vencidoPct ? ` (${decimal(p.cxc.vencidoPct)} %)` : ''}
          </div>
        </Indicador>
        <Indicador titulo="Debemos a proveedores" valor={millones(p.cxp.saldo)}>
          <div className={n(p.cxp.vencido) > 0 ? 'alerta-texto' : undefined}>
            vencido {pesos(p.cxp.vencido)}
          </div>
        </Indicador>
      </div>

      <fieldset className="recuadro-ficha">
        <legend>Venta neta por día · últimos 35 días</legend>
        <GraficoDias dias={v.ultimos35} />
        <p className="apagado nota">
          Factura + boleta + vale − NC.{' '}
          {p.fuentes.inicioErp ? `Desde el ${fecha(p.fuentes.inicioErp)}: ERP. ` : ''}
          Antes: Random (barras claras, las tres empresas)
          {p.fuentes.historicoHasta ? `, cargado hasta el ${fecha(p.fuentes.historicoHasta)}` : ''}.
          Los días sin dato quedan en cero.
        </p>
      </fieldset>

      <div className="tres-columnas">
        <fieldset className="recuadro-ficha">
          <legend>Hoy por canal</legend>
          <TablaGrupos filas={v.porCanal} nombre={(c) => NOMBRE_CANAL[c] ?? c} />
        </fieldset>
        <fieldset className="recuadro-ficha">
          <legend>Hoy por vendedor</legend>
          <TablaGrupos filas={v.porVendedor} nombre={(c) => c} />
        </fieldset>
        <fieldset className="recuadro-ficha">
          <legend>Hoy por documento</legend>
          <TablaGrupos filas={v.porTipo} nombre={(c) => c.charAt(0) + c.slice(1).toLowerCase()} />
        </fieldset>
      </div>

      <div className="tres-columnas">
        <fieldset className="recuadro-ficha">
          <legend>Cobrado hoy · {pesos(String(cobradoHoy))}</legend>
          <table className="grilla compacta">
            <tbody>
              {p.caja.cobradoHoy.map((c) => (
                <tr key={c.medio}>
                  <td>{NOMBRE_MEDIO[c.medio] ?? c.medio}</td>
                  <td className="der">{c.recibos}</td>
                  <td className="der">{pesos(c.monto)}</td>
                </tr>
              ))}
              {!p.caja.cobradoHoy.length && (
                <tr>
                  <td colSpan={3} className="grilla-vacia">
                    Sin cobros hoy
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </fieldset>
        <fieldset className="recuadro-ficha">
          <legend>Cajas abiertas</legend>
          <table className="grilla compacta">
            <thead>
              <tr>
                <th>Caja</th>
                <th>Cajero</th>
                <th className="der">Efectivo</th>
              </tr>
            </thead>
            <tbody>
              {p.caja.cajasAbiertas.map((c) => (
                <tr key={`${c.punto}-${c.abiertoEn}`}>
                  <td>{c.punto}</td>
                  <td className="recortar">{c.cajero}</td>
                  <td className="der">{pesos(c.efectivo)}</td>
                </tr>
              ))}
              {!p.caja.cajasAbiertas.length && (
                <tr>
                  <td colSpan={3} className="grilla-vacia">
                    Ninguna caja abierta
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </fieldset>
        <fieldset className="recuadro-ficha">
          <legend>Lo más vendido hoy</legend>
          <table className="grilla compacta">
            <tbody>
              {v.productos.map((x) => (
                <tr key={x.sku}>
                  <td className="recortar">{x.producto}</td>
                  <td className="der">{decimal(x.unidades)}</td>
                  <td className="der">{pesos(x.neto)}</td>
                </tr>
              ))}
              {!v.productos.length && (
                <tr>
                  <td colSpan={3} className="grilla-vacia">
                    Sin ventas todavía
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </fieldset>
      </div>

      <div className="dos-columnas">
        <TablaCartera c={p.cxc} titulo="Cuentas por cobrar" entidad="Cliente" />
        <TablaCartera c={p.cxp} titulo="Cuentas por pagar" entidad="Proveedor" />
      </div>

      <TablaFlujo f={p.flujo} />

      <fieldset className="recuadro-ficha">
        <legend>Venta neta por mes</legend>
        <table className="grilla compacta" aria-label="Venta neta por mes">
          <tbody>
            {v.meses.map((m) => (
              <tr key={m.mes}>
                <td>
                  {m.mes.slice(5)}-{m.mes.slice(0, 4)}
                </td>
                <td className="der">{pesos(m.neto)}</td>
                <td className="apagado">
                  {m.fuente === 'RANDOM'
                    ? 'Random'
                    : m.fuente === 'MIXTO'
                      ? 'Random + ERP'
                      : m.fuente === 'ERP'
                        ? 'ERP'
                        : 'sin dato'}
                  {m.incompleto && m.fuente !== 'SIN_DATO' ? ' · faltan días' : ''}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </fieldset>
    </div>
  );
}
